import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { History, Pencil, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_STYLES = {
  owner: "bg-rose-50 text-rose-700 border-rose-100",
  purchaser: "bg-emerald-50 text-emerald-700 border-emerald-100",
  admin_input: "bg-teal-50 text-teal-700 border-teal-100",
  admin: "bg-stone-100 text-stone-600 border-stone-200",
  receiving: "bg-amber-50 text-amber-700 border-amber-100",
  viewer: "bg-rose-50 text-rose-600 border-rose-100",
};

const ROLE_OPTIONS = [
  { v: "admin_input", l: "Admin Input / Checker — customers, orders, receiving approvals & shipping" },
  { v: "purchaser", l: "Purchaser — shopping & record purchases" },
  { v: "receiving", l: "Admin Stock / Receiving PIC — receiving queue & sessions" },
  { v: "owner", l: "Owner — full access" },
];

const AUDIT_LABELS = {
  user_created: "User created",
  user_updated: "Profile updated",
  role_changed: "Role changed",
  user_deactivated: "User deactivated",
  user_reactivated: "User reactivated",
  user_deleted: "User permanently deleted",
};

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("purchaser");
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [auditTarget, setAuditTarget] = useState(null);
  const [auditEntries, setAuditEntries] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    if (user.role === "owner") load();
  }, [user, load]);

  if (user.role !== "owner") {
    return (
      <div data-testid="team-forbidden" className="grid place-items-center rounded-md border border-dashed py-20">
        <p className="font-medium text-muted-foreground">Team management is only available to the Owner.</p>
      </div>
    );
  }

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", { name: name.trim(), email: email.trim(), password, role });
      toast.success(`Added ${name.trim()} as ${ROLE_LABELS[role] || role}`);
      setOpen(false);
      setName("");
      setEmail("");
      setPassword("");
      setRole("purchaser");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditName(u.name || "");
    setEditEmail(u.email);
    setEditRole(u.role);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${editTarget.id}`, {
        name: editName.trim(),
        email: editEmail.trim(),
        role: editRole,
      });
      toast.success("User updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name || deleteTarget.email} permanently deleted — history preserved`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const openAudit = async (u) => {
    setAuditTarget(u);
    setAuditEntries(null);
    try {
      const { data } = await api.get(`/users/${u.id}/audit`);
      setAuditEntries(data);
    } catch (e) {
      toast.error(apiError(e));
      setAuditEntries([]);
    }
  };

  const isSelf = (u) => u.id === user.id;

  return (
    <div data-testid="team-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">Manage users, roles and access — Owner only</p>
        </div>
        <Button data-testid="add-member-button" className="h-12 px-5" onClick={() => setOpen(true)}>
          <UserPlus className="h-4 w-4" /> Add User
        </Button>
      </div>

      {users === null ? (
        <div className="grid place-items-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <div data-testid="team-list" className="space-y-2.5">
          {users.map((u) => {
            const inactive = u.is_active === false;
            return (
              <div key={u.id} data-testid={`team-member-${u.id}`} className={`flex items-center gap-2 overflow-hidden rounded-md border bg-card p-3 sm:gap-3 sm:p-4 ${inactive ? "opacity-60" : ""}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent font-heading text-sm font-bold text-accent-foreground">
                  {(u.name || u.email).slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {u.name}
                    {isSelf(u) && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                  <span className={`mt-1 inline-block rounded-md border px-2 py-0.5 text-[11px] font-semibold sm:hidden ${ROLE_STYLES[u.role] || ROLE_STYLES.admin}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <span className={`hidden rounded-md border px-2 py-0.5 text-xs font-semibold sm:inline ${ROLE_STYLES[u.role] || ROLE_STYLES.admin}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                  {inactive && (
                    <span data-testid={`inactive-badge-${u.id}`} className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600">
                      Inactive
                    </span>
                  )}
                  <button
                    data-testid={`edit-member-${u.id}`}
                    aria-label={`Edit ${u.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-accent hover:text-stone-700"
                    onClick={() => openEdit(u)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`audit-member-${u.id}`}
                    aria-label={`Audit history for ${u.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-accent hover:text-stone-700"
                    onClick={() => openAudit(u)}
                  >
                    <History className="h-4 w-4" />
                  </button>
                  {!isSelf(u) && (
                    <button
                      data-testid={`delete-member-${u.id}`}
                      aria-label={`Delete ${u.name}`}
                      className="flex h-8 w-8 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      onClick={() => setDeleteTarget(u)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Add Team Member</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="member-name-input" className="h-12" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="member-email-input" type="email" className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="member-password-input" type="text" className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="member-role-select" className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.v} value={r.v} data-testid={`role-option-${r.v}`}>{r.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="member-save-button"
              className="h-12 w-full text-base"
              onClick={submit}
              disabled={saving || !name.trim() || !email.trim() || password.length < 6}
            >
              {saving ? "Adding…" : "Add Member"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Edit User — {editTarget?.name}</DrawerTitle>
          </DrawerHeader>
          {editTarget && (
            <div className="space-y-4 px-4 pb-8" data-testid="edit-user-form">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input data-testid="edit-name-input" className="h-12" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="edit-email-input" type="email" className="h-12" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole} disabled={isSelf(editTarget)}>
                  <SelectTrigger data-testid="edit-role-select" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(ROLE_OPTIONS.some((r) => r.v === editRole)
                      ? ROLE_OPTIONS
                      : [...ROLE_OPTIONS, { v: editRole, l: `${ROLE_LABELS[editRole] || editRole} — legacy, consider migrating` }]
                    ).map((r) => (
                      <SelectItem key={r.v} value={r.v} data-testid={`edit-role-option-${r.v}`}>{r.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSelf(editTarget) && <p className="text-xs text-muted-foreground">You cannot change your own role.</p>}
              </div>
              <Button
                data-testid="save-member-edit-button"
                className="h-12 w-full text-base"
                onClick={saveEdit}
                disabled={saving || !editName.trim() || !editEmail.trim()}
              >
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name || deleteTarget?.email} will no longer be able to log in. Historical activity and audit records will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={deleteUser}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Drawer open={!!auditTarget} onOpenChange={(o) => !o && setAuditTarget(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">User Audit — {auditTarget?.name}</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto px-4 pb-8" data-testid="user-audit-list">
            {auditEntries === null ? (
              <div className="grid place-items-center py-10">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : auditEntries.length === 0 ? (
              <p className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">No audit entries yet</p>
            ) : (
              auditEntries.map((e) => (
                <div key={e.id} data-testid={`user-audit-${e.id}`} className="rounded-md border bg-card p-3">
                  <p className="text-xs font-semibold">
                    {AUDIT_LABELS[e.action] || e.action}
                    {e.field && e.action !== "user_created" && (
                      <span className="text-muted-foreground"> — {e.field}: {String(e.old_value)} → {String(e.new_value)}</span>
                    )}
                    {e.action === "user_created" && e.new_value && <span className="text-muted-foreground"> as {ROLE_LABELS[e.new_value] || e.new_value}</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    by {e.actor_name} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                  </p>
                </div>
              ))
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
